import { keccak_256 } from "@noble/hashes/sha3.js";

import { readDexSdk } from "@/contexts/dex-sdk";

import { CCTPTransientError } from "../errors";
import { APTOS_CCTP_DOMAIN_ID, bytesToHex, getMessageTransmitterContract } from "./contracts";

/**
 * Replicates the Aptos CCTP MessageTransmitter's `hash_source_and_nonce`:
 *   key = bcs::to_bytes(source_domain) + "-" + bcs::to_bytes(nonce)
 *   hash = keccak256(key)
 *   address = from_bcs::to_address(hash)
 *
 * BCS uses little-endian encoding for integers.
 */
function hashSourceAndNonce(sourceDomain: number, nonce: number): string {
  // BCS u32: 4 bytes little-endian
  const sourceBytes = new Uint8Array(4);
  new DataView(sourceBytes.buffer).setUint32(0, sourceDomain, true);

  // BCS u64: 8 bytes little-endian
  const nonceBytes = new Uint8Array(8);
  const nonceView = new DataView(nonceBytes.buffer);
  nonceView.setUint32(0, nonce >>> 0, true);
  nonceView.setUint32(4, Math.floor(nonce / 0x100000000), true);

  // [u32 LE] + "-" + [u64 LE] = 13 bytes
  const combined = new Uint8Array(13);
  combined.set(sourceBytes, 0);
  combined[4] = 0x2d; // "-"
  combined.set(nonceBytes, 5);

  const hash = keccak_256(combined);
  return "0x" + bytesToHex(hash);
}

/**
 * Check if a CCTP nonce has been consumed on Aptos by querying the
 * MessageTransmitter contract's `is_nonce_used` view function on-chain.
 *
 * Throws `CCTPTransientError` on RPC failure — silently coercing to `false`
 * lets stale transfers import as "bridging" during an outage.
 */
export async function checkNonceUsedOnAptos(sourceDomain: number, nonce: number): Promise<boolean> {
  const hashAddress = hashSourceAndNonce(sourceDomain, nonce);
  const pkg = getMessageTransmitterContract(APTOS_CCTP_DOMAIN_ID);
  if (!pkg) return false;

  try {
    const [isUsed] = await readDexSdk.deps.aptos.view<[boolean]>({
      payload: {
        function: `${pkg}::message_transmitter::is_nonce_used`,
        typeArguments: [],
        functionArguments: [hashAddress],
      },
    });
    return isUsed;
  } catch (err) {
    throw new CCTPTransientError(
      `Aptos nonce check failed (sourceDomain=${sourceDomain}, nonce=${nonce})`,
      err,
    );
  }
}
